import apiSkobo from "@/helpers/api/apiSkobo";
import { useSettingsStore } from "@/stores/settings";

export const sections_state = {
  mySectionsMap: null,
  myArchivedSectionGroupsMap: {},
  allSectionsOnInstitutionState: null
};

export const sections_actions = {
  /**
   * Load sections for current user if they are not in state
   * @param { string } sectionId SectionId to ensure is loaded (e.g. if it is an archived section and only current sections is available)
   */
  async loadMySections(sectionId) {
    if (this.mySectionsMap && (!sectionId || (sectionId && this.mySectionsMap[sectionId]))) {
      return;
    }

    await this.reloadMySections(sectionId);
  },

  /**
   * Reload sections no matter if the are in state or not
   * @param { string } sectionId SectionId to ensure is loaded (e.g. if it is an archived section and only current sections is available)
   */
  async reloadMySections(sectionId) {
    const response = await apiSkobo.get("/me/section/?options=teachers,students,teams");

    const sections = {};

    response.data.map(sect => {
      sections[sect.sectionId] = {
        sectionId: sect.sectionId,
        name: sect.name,
        institutionId: sect.institutionId,
        extSysApiSectionId: sect.extSysApiSectionId,
        hasConfidentialFoldersForUser: sect.hasConfidentialFoldersForUser,

        teachers: sect.teachers
          ? sect.teachers.map(te => {
              return {
                userId: te.userId,
                extSysApiUserId: te.extSysApiUserId,
                name: te.name,
                emailSchoolAddress: te.emailSchoolAddress
              };
            })
          : [],

        students: sect.students
          ? sect.students.map(st => {
              return {
                userId: st.userId,
                extSysApiUserId: st.extSysApiUserId,
                name: st.name,
                emailSchoolAddress: st.emailSchoolAddress
              };
            })
          : []
      };

      if (sect.teams) {
        const sectionTeam = sect.teams.find(t => t.group === "Provided.O365.Teams.SCH");
        if (
          sectionTeam &&
          sectionTeam.additionalData &&
          sectionTeam.additionalData.webUrl &&
          sectionTeam.additionalData.webUrl.length > 0
        ) {
          sections[sect.sectionId]["teamsUrl"] = sectionTeam.additionalData.webUrl;
        }
      }
    });

    this.mySectionsMap = sections;

    if (sectionId && !sections[sectionId]) {
      this.loadMyArchivedSections();
    }
  },

  /**
   * Load archived sections for current user if they are not in state
   *
   */
  async loadMyArchivedSections() {
    if (this.myArchivedSectionGroupsMap.length > 0) {
      return;
    }
    await this.reloadMyArchivedSections();
  },

  /**
   * Reload archived sections no matter if the are in state or not
   *
   */
  async reloadMyArchivedSections() {
    const response = await apiSkobo.get("/v2/me/section/archived/teachers,students,teams");

    const resultingSectionGroups = [];

    const groups = response.data.reduce((acc, curr) => {
      if (!acc[curr.group]) acc[curr.group] = []; //If this type wasn't previously stored
      acc[curr.group].push(curr);
      return acc;
    }, {});

    for (let key in groups) {
      let title = key.replace("Year roll", "").trim();
      const regex = /(2\d{1})/gm;
      title = title.replaceAll(regex, "20$1");

      const sections = {};

      groups[key].map(sect => {
        sections[sect.sectionId] = {
          sectionId: sect.sectionId,
          name: sect.name,
          institutionId: sect.institutionId,
          extSysApiSectionId: sect.extSysApiSectionId,
          archived: true,

          teachers: sect.teachers
            ? sect.teachers.map(te => {
                return {
                  userId: te.userId,
                  extSysApiUserId: te.extSysApiUserId,
                  name: te.name,
                  emailSchoolAddress: te.emailSchoolAddress
                };
              })
            : [],

          students: sect.students
            ? sect.students.map(st => {
                return {
                  userId: st.userId,
                  extSysApiUserId: st.extSysApiUserId,
                  name: st.name,
                  emailSchoolAddress: st.emailSchoolAddress
                };
              })
            : []
        };

        // Teams url
        const sectionTeam = sect.teams.find(t => t.group === "Provided.O365.Teams.SCH");
        if (sectionTeam?.additionalData?.webUrl?.length > 0) {
          sections[sect.sectionId]["teamsUrl"] = sectionTeam.additionalData.webUrl;
        }
      });

      resultingSectionGroups.push({ title, sections });
    }

    const settingsStore = useSettingsStore();
    resultingSectionGroups.sort((a, b) => b.title.localeCompare(a.title, settingsStore.language));
    this.myArchivedSectionGroupsMap = resultingSectionGroups;
  },

  async loadAllSectionsOnInstitution() {
    if (!this.allSectionsOnInstitutionState) {
      const response = await apiSkobo.get("/Institution/Section");
      this.allSectionsOnInstitutionState = response.data;
    }
  }
};

export const sections_getters = {
  mySections: state => state.mySectionsMap,
  myArchivedSections: state => state.myArchivedSectionGroupsMap,

  groupingTitleForArchivedSection: state => sectionId => {
    for (let i = 0; i < state.myArchivedSectionGroupsMap.length; i++) {
      if (Object.keys(state.myArchivedSectionGroupsMap[i].sections).indexOf(sectionId) > -1) {
        return state.myArchivedSectionGroupsMap[i].title;
      }
    }
  },

  section: state => sectionId => (state.mySectionsMap ? state.mySectionsMap[sectionId] : null),
  allSectionsOnInstitution: state => state.allSectionsOnInstitutionState,
  peopleInMySections: state =>
    state.mySectionsMap ? Object.values(state.mySectionsMap).flatMap(s => [...s.students, ...s.teachers]) : null,

  mySectionsFromSearch: state => {
    return searchTerm => {
      const term = searchTerm.toLowerCase();
      const sectionsArray = Object.values(state.mySectionsMap);
      return sectionsArray.filter(p => p.name.toLowerCase().includes(term));
    };
  },

  allStudentsAsPeopleTableObjectsFromSectionsSearched: state => {
    return searchTerm => {
      const filteredSections = state.mySectionsFromSearch(searchTerm);

      return filteredSections.flatMap(s => {
        return s.students.map(u => ({
          name: { name: u.name.name, firstName: u.name.firstName, lastName: u.name.lastName },
          userId: u.userId,
          sectionId: s.sectionId,
          sectionName: s.name
        }));
      });
    };
  },

  allMyStudentsSearchedAsPeopleTableObejcts: state => {
    return searchTerm => {
      const sectionsArray = Object.values(state.mySectionsMap);
      return sectionsArray.flatMap(s => {
        return s.students
          .filter(u => u.name.name.toLowerCase().includes(searchTerm))
          .map(u => ({
            name: { name: u.name.name, firstName: u.name.firstName, lastName: u.name.lastName },
            userId: u.userId,
            sectionId: s.sectionId,
            sectionName: s.name
          }));
      });
    };
  },

  sectionIdForPersonId: state => {
    return personId => {
      let section;

      if (!state.mySectionsMap) {
        return;
      }

      Object.keys(state.mySectionsMap).forEach(secId => {
        if (state.mySectionsMap[secId].students.find(st => st.userId === personId)) {
          section = state.mySectionsMap[secId];
          return section;
        }
      });
    };
  }

  //Martin: Der mangler en funktion til at søge i alle børnene på alle klasser - og byg så ConfidentialPeopleSearch om til at kunne bruge dem
};
